import { createFileRoute, redirect } from '@tanstack/react-router'

import { documentsShape } from '@/components/documents/documents-search-schema'
import { ProjectDetail } from '@/components/projects/project-detail'
import { useFollowProjectClient } from '@/hooks/use-current-client'
import { RETURN_SHAPE } from '@/lib/return-to'
import { exactly, oneOf, readSearch, text } from '@/lib/search-params'

const SHAPE = {
  /**
   * Onglet de la fiche : vue d'ensemble (par défaut), sprints (lot 9) ou
   * documents (1.33.0). Facultatif : un lien vers un projet n'a pas à le nommer.
   */
  tab: oneOf([
    'apercu',
    'sprints',
    'documents',
    'questionnaires',
    'discussion',
    'preuves',
    'secrets',
    'formations',
  ] as const),
  /**
   * 1.69.0 : l'adresse des courriels du fil (`?onglet=discussion`), la même
   * qu'au portail ; elle ouvre l'onglet « Discussion ».
   */
  onglet: exactly('discussion'),
  // Filtres de l'onglet Documents, les mêmes que chez le client.
  ...documentsShape,
  /**
   * Ancienne adresse d'un sprint (lot 19, `web_path` d'avant #286) : elle
   * redirige vers la page du sprint, pour les liens et courriels déjà partis.
   */
  sprint: text(),
  /** Lot 33 : la fiche d'un secret dans l'onglet « Secrets ». */
  secret: text(),
  ...RETURN_SHAPE,
}

export const Route = createFileRoute('/_app/projects/$projectRef')({
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  beforeLoad: ({ params, search }) => {
    if (!search.sprint) return
    const { sprint, tab: _tab, ...rest } = search
    throw redirect({
      to: '/projects/$projectRef/sprints/$sprintId',
      params: { projectRef: params.projectRef, sprintId: sprint },
      search: { retour: rest.retour },
      replace: true,
    })
  },
  component: ProjectDetailPage,
})

function ProjectDetailPage() {
  const { projectRef } = Route.useParams()
  useFollowProjectClient(projectRef)
  const { tab, onglet, sprint: _sprint, secret, ...search } = Route.useSearch()
  const navigate = Route.useNavigate()
  // La clé de la fiche change avec la référence : pas d'état d'un projet à l'autre.
  return (
    <ProjectDetail
      key={projectRef}
      projectRef={projectRef}
      tab={tab ?? onglet ?? 'apercu'}
      documentsSearch={search}
      secretId={secret}
      onNavigate={(next) => {
        void navigate({
          search: (previous) => ({ ...previous, onglet: undefined, ...next }),
          replace: true,
        })
      }}
    />
  )
}
